import {
    isFailurePolicyRule,
    isMissionStepFailureCondition,
    FAILURE_POLICY_ACTION_TYPES,
} from "lib/consts/missionStepFailure";
import {
    isMissionLightsCondition,
    isMissionLightsRule,
} from "lib/consts/missionLights";
import {
    isMissionAudioCondition,
    isMissionAudioRule,
} from "lib/consts/missionAudio";
import { isRetryActionType } from "lib/consts/behaviourRules";

const formatActionLabel = (rule, catalog) => {
    const action = catalog?.actions?.find((item) => item.type === rule?.action?.type);
    const params = rule?.action?.params ?? {};
    if (isMissionAudioRule(rule)) {
        const trackName = params.track_name || "audio";
        const interval = params.interval_sec || "30";
        const volume = params.volume || "80";
        return `Play ${trackName} every ${interval}s @ ${volume}%`;
    }
    if (isMissionLightsRule(rule)) {
        const lightTypes = rule.lightTypes?.length
            ? rule.lightTypes
            : String(params.lights || "")
                  .split(",")
                  .map((item) => item.trim())
                  .filter(Boolean);
        const labels = (catalog?.mission_light_types ?? [])
            .filter((item) => lightTypes.includes(item.light_type))
            .map((item) => item.label);
        if (labels.length > 0) {
            return `Turn on ${labels.join(", ")}`;
        }
        if (lightTypes.length > 0) {
            return `Turn on ${lightTypes.join(", ")}`;
        }
        return "Turn on lights";
    }
    if (isRetryActionType(rule?.action?.type)) {
        const retryCount = params.retry_count || "1";
        const exhaustedAction = catalog?.actions?.find(
            (item) =>
                item.type === params.on_exhausted ||
                (params.on_exhausted === "rtl" &&
                    item.type === FAILURE_POLICY_ACTION_TYPES.RTL) ||
                (params.on_exhausted === "stop" &&
                    item.type === FAILURE_POLICY_ACTION_TYPES.STOP) ||
                (params.on_exhausted === "continue" &&
                    item.type === FAILURE_POLICY_ACTION_TYPES.CONTINUE),
        );
        const exhaustedLabel =
            exhaustedAction?.label ??
            (params.on_exhausted === "rtl" || params.on_exhausted === "return_to_launch"
                ? "Return to launch"
                : params.on_exhausted === "continue"
                  ? "Continue mission"
                  : params.on_exhausted === "stop"
                    ? "Stop mission"
                    : "Stop and hold position");
        return `Retry ${retryCount}x, then ${exhaustedLabel}`;
    }
    if (rule?.action?.type === FAILURE_POLICY_ACTION_TYPES.PLAN_NEXT) {
        const skipMax = params.plan_next_max || "3";
        const exhaustedLabel =
            params.on_exhausted === "continue"
                ? "Continue mission"
                : params.on_exhausted === "stop"
                  ? "Stop mission"
                  : "Return to launch";
        const followOn =
            params.skip_follow_on_actions === "false"
                ? ""
                : "; also skip Take photo/Wait";
        return `Skip up to ${skipMax}x, then ${exhaustedLabel}${followOn}`;
    }
    if (action?.label) {
        const mode = rule?.action?.params?.mode;
        if (mode) {
            const modeLabel =
                catalog?.failure_policy_rtl_modes?.find((item) => item.value === mode)?.label ??
                mode.replace(/_/g, " ");
            return `${action.label} (${modeLabel})`;
        }
        return action.label;
    }
    return (rule?.action?.type ?? "—").replace(/_/g, " ");
};

const formatConditionLabel = (rule, catalog) => {
    const condition = rule?.conditions?.items?.[0];
    if (!condition) return "No condition";
    const source = catalog?.event_sources?.find(
        (item) => item.topic === condition.topic && item.field === condition.field,
    );
    if (source?.label) return source.label;
    if (isMissionStepFailureCondition(condition)) {
        return `${condition.field} failed`;
    }
    if (isMissionLightsCondition(condition)) {
        return "Mission running";
    }
    if (isMissionAudioCondition(condition)) {
        return "Mission running (audio)";
    }
    return condition.field || "Condition";
};

const RuleToggle = ({ enabled, onToggle, disabled, label }) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={label}
        disabled={disabled}
        onClick={onToggle}
        className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-0.5 transition-colors duration-200 ease-in-out disabled:cursor-not-allowed disabled:opacity-50 ${
            enabled
                ? "justify-end border-primary2 bg-primary2"
                : "justify-start border-black70 bg-black80"
        }`}
    >
        <span className="pointer-events-none block h-4 w-4 shrink-0 rounded-full bg-white shadow" />
    </button>
);

const BehaviourRuleCard = ({ rule, catalog, onEdit, onDelete, onToggleEnabled, isBusy }) => {
    const conditionCount = rule?.conditions?.items?.length ?? 0;
    const failurePolicyRule = isFailurePolicyRule(rule);
    const missionLightsRule = isMissionLightsRule(rule);
    const missionAudioRule = isMissionAudioRule(rule);
    const summary =
        failurePolicyRule || missionLightsRule || missionAudioRule
            ? `${formatConditionLabel(rule, catalog)} → ${formatActionLabel(rule, catalog)}`
            : `${conditionCount} condition${conditionCount === 1 ? "" : "s"} → ${formatActionLabel(rule, catalog)}`;

    return (
        <div className="flex w-full flex-col gap-2 rounded-r5 border border-black70 bg-black90 p-3">
            <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 flex-1 truncate text-h5 font-semibold">{rule.name}</p>
                <div className="flex shrink-0 items-center gap-2">
                    <RuleToggle
                        enabled={Boolean(rule.enabled)}
                        disabled={isBusy}
                        label={`Toggle rule ${rule.name}`}
                        onToggle={() => onToggleEnabled(rule)}
                    />
                    <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 border border-black70 bg-black90 hover:bg-black80"
                        onClick={() => onEdit(rule)}
                        disabled={isBusy}
                        aria-label={`Edit rule ${rule.name}`}
                    >
                        ✎
                    </button>
                    <button
                        type="button"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-r5 border border-black70 bg-black90 hover:bg-black80"
                        onClick={() => onDelete(rule)}
                        disabled={isBusy}
                        aria-label={`Delete rule ${rule.name}`}
                    >
                        ×
                    </button>
                </div>
            </div>
            <p className="min-w-0 text-h5 text-black40 line-clamp-2">{summary}</p>
        </div>
    );
};

export default BehaviourRuleCard;
